<template>
  <div class="poster-content">
    <div class="poster-body">
      <div class="poster-card" v-for="(item, i) in posterList" :key="i" @click.stop="jumpProductUrl(item.productId)">
        <div class="poster-title">{{ item.name }}</div>
        <div class="scroll-box" :class="{ 'is-open': item.showToggle && !item.close }">
          <div class="copywriting-warp" :class="{ 'is-open': item.showToggle && !item.close }">
            <!-- 文案内容：超过3行(54px)且为收起状态时，限制高度并隐藏溢出内容 -->
            <div class="poster-copywriting" :class="{ 'is-close': item.showToggle && item.close }">{{ item.copywriting }}</div>
            <!-- 展开按钮：仅在文案超长且处于收起状态时显示 -->
            <div class="open-btn" v-if="item.showToggle && item.close" @click.stop="toggleClose(item)">
              <span>展开</span>
              <i class="iconfont icon-xiala"></i>
            </div>
            <!-- 收起按钮：仅在文案超长且处于展开状态时显示 -->
            <div class="open-btn" v-else-if="item.showToggle && !item.close" @click.stop="toggleClose(item)">
              <span>收起</span>
              <i class="iconfont icon-shangshou"></i>
            </div>
          </div>
          <div class="img-list">
            <!-- 图片列表：点击打开预览弹窗 -->
            <img
              v-for="(img, index) in item.imgList"
              v-if="index < 3"
              :key="img.url"
              :src="getThumbnailUrl(img.url)"
              class="poster-img"
              alt=""
              loading="lazy"
              decoding="async"
              @error="handleThumbnailError($event, img.url)"
              @click.stop="openImagePreview(item, index)"
            />
          </div>
        </div>
        <div class="btn-list">
          <div class="btn" @click.stop="copyText(item.copywriting)" v-if="item.copywriting">复制文案</div>
          <div class="btn org" @click.stop="downloadImg(item)">下载图片</div>
        </div>
      </div>
      <!-- 图片预览弹窗组件 -->
      <image-preview-dialog
        :visible.sync="imageDialog"
        :images="previewImages"
        :detail="currentDetail"
        :initial-index="currentImageIndex"
        @copy-text="copyText"
      />
    </div>
  </div>
</template>
<script>
// 引入图片预览弹窗组件
import ImagePreviewDialog from './imagePreviewDialog.vue';

export default {
  // 注册组件
  components: {
    ImagePreviewDialog
  },
  props: {
    list: null
  },
  data: () => ({
    textDialog: false, // 文案弹窗显示状态（保留兼容）
    detail: {}, // 当前查看的详情数据
    activeNum: 0,
    // 海报列表（内部维护，附带展开/收起状态字段）
    posterList: [],
    // 图片预览弹窗相关
    imageDialog: false, // 图片预览弹窗显示状态
    previewImages: [], // 待预览的图片URL数组（取自imgList）
    currentDetail: {}, // 当前预览的海报详情（用于复制文案）
    currentImageIndex: 0 // 点击的图片索引，传递给预览弹窗作为初始索引
  }),
  watch: {
    /**
     * 监听外部 list 变化，重新构建内部列表并初始化展开收起状态
     */
    list: {
      handler() {
        // 构建内部列表：close 默认为收起状态，showToggle 待测量后决定
        this.posterList = this.list.map(item => {
          return {
            ...item,
            // 图片列表，由 url 字段反序列化得到
            imgList: item.url ? JSON.parse(item.url) : [],
            // 收起状态标识：true 表示收起，false 表示展开
            close: true,
            // 是否显示展开/收起按钮：仅在文案超过 3 行(54px)时为 true
            showToggle: false
          };
        });
        // DOM 渲染完成后测量文案高度并设置 scroll-box 高度
        this.$nextTick(() => {
          this.checkCopywritingHeight();
          this.setScrollBoxHeight();
        });
      },
      immediate: true
    }
  },
  created() {},
  /**
   * 组件挂载完成：注册 window resize 监听，窗口尺寸变化时重新计算 scroll-box 高度
   */
  mounted() {
    // 防抖处理：避免 resize 事件高频触发导致的性能问题
    this.resizeDebounce = null;
    window.addEventListener('resize', this.handleResize);
  },
  /**
   * 组件销毁前：移除 resize 监听，防止内存泄漏
   */
  beforeDestroy() {
    window.removeEventListener('resize', this.handleResize);
    // 清理可能残留的定时器
    if (this.resizeDebounce) {
      clearTimeout(this.resizeDebounce);
      this.resizeDebounce = null;
    }
  },
  methods: {
    /**
     * 获取列表缩略图地址，预览和下载仍使用原图地址
     * @param {String} url - 原图地址
     * @returns {String} OSS 缩略图地址
     */
    getThumbnailUrl(url) {
      if (!url || typeof url !== 'string' || url.indexOf('x-oss-process=') !== -1) {
        return url;
      }

      const hashIndex = url.indexOf('#');
      const hash = hashIndex === -1 ? '' : url.slice(hashIndex);
      const baseUrl = hashIndex === -1 ? url : url.slice(0, hashIndex);
      const separator = baseUrl.indexOf('?') === -1 ? '?' : '&';

      return `${baseUrl}${separator}x-oss-process=image/resize,m_lfit,w_480,limit_1/quality,q_75/format,webp${hash}`;
    },
    /**
     * OSS 图片处理不可用时回退原图，避免列表出现空白图片
     * @param {Event} event - 图片加载失败事件
     * @param {String} originalUrl - 原图地址
     */
    handleThumbnailError(event, originalUrl) {
      const image = event.currentTarget;
      if (!image || !originalUrl || image.dataset.originalLoaded) {
        return;
      }

      image.dataset.originalLoaded = 'true';
      image.src = originalUrl;
    },
    /**
     * 打开图片预览弹窗
     * @param {Object} item - 当前点击的海报项
     * @param {Number} index - 点击的图片索引
     */
    openImagePreview(item, index) {
      // 保存当前海报详情（用于复制文案）
      this.currentDetail = item;
      // 从 imgList 提取图片URL数组
      this.previewImages = item.imgList ? item.imgList.map(img => img.url) : [];
      // 记录点击的图片索引，传递给预览弹窗
      this.currentImageIndex = index;
      // 显示弹窗
      this.imageDialog = true;
    },
    /**
     * 跳转产品详情页
     * @param {String} productId - 产品ID
     */
    jumpProductUrl(productId) {
      this.$emit('jumpProductUrl', productId);
    },
    downloadImg(item) {
      this.$tools.daochu(
        '/master/information/downloadFileByUrl',
        {
          url: item.url
        },
        'PUT'
      );
    },
    copyText(text) {
      this.$tools.copyUrl(text);
      this.textDialog = false;
    },
    /**
     * window resize 事件处理：防抖触发，重新计算 scroll-box 高度
     */
    handleResize() {
      // 防抖：每次触发时清除上一次定时器，仅在停止触发 200ms 后执行
      if (this.resizeDebounce) {
        clearTimeout(this.resizeDebounce);
      }
      this.resizeDebounce = setTimeout(() => {
        this.setScrollBoxHeight();
        this.resizeDebounce = null;
      }, 200);
    },
    /**
     * 检测每条文案的真实高度，超过 3 行(54px)则标记需要显示展开/收起按钮
     */
    checkCopywritingHeight() {
      // 获取所有文案 DOM 节点
      const els = this.$el.querySelectorAll('.poster-copywriting');
      if (!els || !els.length) return;
      els.forEach((el, index) => {
        // scrollHeight 为元素完整内容高度，不受 max-height 限制
        const realHeight = el.scrollHeight;
        // 高度超过 54px(3 行)则显示按钮
        if (realHeight > 56) {
          // 当前阶段 showToggle 初始为 false，未应用 is-close 类，测量值为真实高度
          this.$set(this.posterList[index], 'showToggle', true);
        }
      });
    },
    /**
     * 设置 scroll-box 的固定高度
     * 高度计算公式：54px(文案3行收起高度) + 10px(文案下间距) + img-list 元素实际渲染高度
     * 说明：img-list 高度由图片 aspect-ratio(9/16) 决定，依赖容器宽度，无法用纯 CSS 在 height 中引用宽度，故采用 JS 测量
     */
    setScrollBoxHeight() {
      // 获取所有 scroll-box DOM 节点
      const scrollBoxEls = this.$el.querySelectorAll('.scroll-box');
      if (!scrollBoxEls || !scrollBoxEls.length) return;
      scrollBoxEls.forEach(scrollBox => {
        // 查询当前 scroll-box 内的 img-list 元素
        const imgList = scrollBox.querySelector('.img-list');
        if (!imgList) return;
        // 读取 img-list 实际渲染高度(offsetHeight，包含 padding/border)
        const imgListHeight = imgList.offsetHeight;
        // 设置 scroll-box 高度：54 + 10 + img-list 实际高度
        scrollBox.style.height = `${54 + 10 + imgListHeight}px`;
      });
    },
    /**
     * 切换文案的展开/收起状态
     * @param {Object} item - 当前点击的海报项
     */
    toggleClose(item) {
      item.close = !item.close;
    }
  }
};
</script>
<style lang="scss" scoped>
@import './posterCenter';
</style>
